import React, { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import './AdminTable.css'
import 'bootstrap-icons/font/bootstrap-icons.css'

const FIELD_LABELS = {
    name: 'Jméno',
    discord_id: 'Discord',
    activity: 'Aktivita',
    notes: 'Poznámky',
    strikes: 'Strikes',
    position: 'Pozice',
    hired: "Přijat"
}

const POSITION_OPTIONS = {
    0: 'Test Admin',
    1: 'Junior Admin',
    2: 'Admin',
    3: 'Senior Admin',
    4: 'Supervisor Junior Admin'
}

const getPositionColor = pos => {
    switch (pos) {
        case 0: return '#adb5bd'
        case 1: return '#00d1b2'
        case 2: return '#ffdd57'
        case 3: return '#ff851b'
        case 4: return '#2ecc71'
        default: return '#ffffff'
    }
}

const formatDate = isoDate => {
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return isoDate;
    return `${day}.${month}.${year}`;
};

const getStrikeColor = strikes => strikes <= 2 ? '#28a745' : strikes <= 5 ? '#ffc107' : '#dc3545'

const getActivityEmoji = val => {
    const map = {
        0: '😴', 1: '😪', 2: '😐', 3: '🙂', 4: '😊',
        5: '😃', 6: '😄', 7: '🤩', 8: '🚀', 9: '🏆', 10: '👑'
    }
    return map[val] || '❓'
}

const AdminTable = ({ isEditable = true }) => {
    const [admins, setAdmins] = useState([])
    const API_URL = 'https://api.lexikonn.eu/admin/api.php'

    const fetchAdmins = useCallback(async () => {
        try {
            const res = await fetch(API_URL)
            if (!res.ok) throw new Error('Fetch failed')
            let data = await res.json()
            data = data.map(item => ({
                ...item,
                position: Number(item.position),
                strikes: Number(item.strikes),
                activity: Number(item.activity),
                hired: item.hired
            }))
            data.sort((a, b) => b.position - a.position)
            setAdmins(data)
        } catch (err) {
            console.error(err)
            Swal.fire('Chyba', 'Nepodařilo se načíst administrátory', 'error')
        }
    }, [])

    useEffect(() => { fetchAdmins() }, [fetchAdmins])

    const handleEditField = useCallback(async (admin, field) => {
        if (!isEditable) return
        let updated = { ...admin }
        if (field === 'notes') {
            const { value } = await Swal.fire({
                title: `Upravit ${FIELD_LABELS.notes}`,
                input: 'textarea', inputValue: admin.notes || '',
                showCancelButton: true, confirmButtonText: 'Uložit', cancelButtonText: 'Zrušit'
            })
            if (value !== undefined) updated.notes = value
        }
        else if (field === 'position') {
            const { value } = await Swal.fire({
                title: `Upravit ${FIELD_LABELS.position}`,
                input: 'select', inputOptions: POSITION_OPTIONS, inputValue: String(admin.position),
                showCancelButton: true, confirmButtonText: 'Uložit', cancelButtonText: 'Zrušit'
            })
            if (value !== undefined) updated.position = Number(value)
        }
        else if (field === 'activity') {
            const { value } = await Swal.fire({
                title: `Upravit ${FIELD_LABELS.activity}`,
                html: `<div style="display:flex;align-items:center;gap:12px;">
                 <input id="swal-slider" type="range" min="0" max="10" value="${admin.activity}" style="flex:1;" />
                 <span id="swal-emoji" style="font-size:1.5em;">${getActivityEmoji(admin.activity)}</span>
               </div>`,
                showCancelButton: true, confirmButtonText: 'Uložit', cancelButtonText: 'Zrušit',
                didOpen: () => {
                    const slider = document.getElementById('swal-slider')
                    const emojiEl = document.getElementById('swal-emoji')
                    slider.addEventListener('input', e => { emojiEl.textContent = getActivityEmoji(Number(e.target.value)) })
                },
                preConfirm: () => Number(document.getElementById('swal-slider').value)
            })
            if (value !== undefined) updated.activity = value
        }
        else {
            const { value } = await Swal.fire({
                title: `Upravit ${FIELD_LABELS[field]}`,
                input: 'text', inputValue: String(admin[field] ?? ''),
                showCancelButton: true, confirmButtonText: 'Uložit', cancelButtonText: 'Zrušit'
            })
            if (value !== undefined) updated[field] = field==='strikes'?Number(value):value
        }
        try {
            const res = await fetch(API_URL, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(updated) })
            if (!res.ok) throw new Error('Update failed')
            setAdmins(prev => prev.map(a => a.id===admin.id ? updated : a).sort((a,b)=>b.position-a.position))
            Swal.fire('Upraveno', `${FIELD_LABELS[field]} u “${admin.name}” bylo uloženo`, 'success')
        } catch (err) {
            console.error(err)
            Swal.fire('Chyba', `Nepodařilo se uložit ${FIELD_LABELS[field]}`, 'error')
        }
    }, [isEditable])

    const handleDelete = useCallback(async admin => {
        if (!isEditable) return
        const r = await Swal.fire({ title:`Opravdu smazat “${admin.name}”?`, icon:'warning', showCancelButton:true, confirmButtonText:'Ano', cancelButtonText:'Ne' })
        if (!r.isConfirmed) return
        try {
            const res = await fetch(API_URL, { method:'DELETE', headers:{'Content-Type':'application/json'}, body:JSON.stringify({id:admin.id}) })
            if (!res.ok) throw new Error()
            setAdmins(prev=>prev.filter(a=>a.id!==admin.id))
            Swal.fire('Smazáno','Administrátor odstraněn','success')
        } catch { Swal.fire('Chyba','Nepodařilo se smazat','error') }
    }, [isEditable])

    const handleAdd = useCallback(async () => {
        if (!isEditable) return
        const { value:name } = await Swal.fire({ title:'Nový administrátor', input:'text', inputPlaceholder:'Jméno', showCancelButton:true, confirmButtonText:'Vytvořit' })
        if (!name) return
        const newA = { name, discord_id:null, activity:0, notes:'', strikes:0, position:2 }
        try {
            const res = await fetch(API_URL, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(newA) })
            if (!res.ok) throw new Error()
            const created = await res.json()
            setAdmins(prev=>[...prev,created].sort((a,b)=>b.position-a.position))
            Swal.fire('Přidáno',`Administrátor “${created.name}” vytvořen`,'success')
        } catch { Swal.fire('Chyba','Nepodařilo se vytvořit','error') }
    }, [isEditable])

    return (
        <div className="admin-table-container">
            {isEditable && <button className="btn-add" onClick={handleAdd}><i className="bi bi-plus-lg"/> Přidat</button>}
            <div className="table-wrapper">
                <table className="admin-table" style={{ textAlign: 'center' }}>
                    <thead>
                    <tr>
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.name}</th>
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.discord_id}</th>
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.activity}</th>
                        {isEditable && <th style={{ textAlign: 'center' }}>{FIELD_LABELS.notes}</th>}
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.strikes}</th>
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.position}</th>
                        <th style={{ textAlign: 'center' }}>{FIELD_LABELS.hired}</th>
                        {isEditable && <th style={{ textAlign: 'center' }}>Akce</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {admins.map(admin => (
                        <tr key={admin.id}>
                            <td style={{ color: getPositionColor(admin.position) }}>
                                {isEditable && <i className="bi bi-pencil" onClick={()=>handleEditField(admin,'name')} style={{cursor:'pointer',marginRight:8}}/>}
                                {admin.name}
                            </td>
                            <td>
                                {isEditable && <i className="bi bi-pencil" onClick={()=>handleEditField(admin,'discord_id')} style={{cursor:'pointer',marginRight:8}}/>}
                                {admin.discord_id
                                    ? <a href={`https://discord.com/users/${admin.discord_id}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-discord" style={{fontSize:'1.2em',color:'#7289da'}}/></a>
                                    : '-'}
                            </td>
                            <td onClick={()=>handleEditField(admin,'activity')} style={{cursor:isEditable?'pointer':'default',fontSize:20,textAlign:'center'}}>
                                {getActivityEmoji(admin.activity)}
                            </td>
                            {isEditable && (
                                <td>
                                    <i className="bi bi-pencil" onClick={()=>handleEditField(admin,'notes')} style={{cursor:'pointer',marginRight:8}}/>
                                    <span onClick={()=>Swal.fire({title:`${FIELD_LABELS.notes} u “${admin.name}”`,html:`<div style="white-space:pre-wrap; text-align:left;">${admin.notes.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>`,icon:'info'})} style={{cursor:'pointer'}}>
                      {admin.notes.length>20 ? admin.notes.slice(0,20)+'...' : admin.notes}
                    </span>
                                </td>
                            )}
                            <td style={{color:getStrikeColor(admin.strikes),fontWeight:600}}>
                                {isEditable && <i className="bi bi-pencil" onClick={()=>handleEditField(admin,'strikes')} style={{cursor:'pointer',marginRight:8}}/>}
                                {admin.strikes}
                            </td>
                            <td style={{color:getPositionColor(admin.position),fontWeight:600}}>
                                {isEditable && <i className="bi bi-pencil" onClick={()=>handleEditField(admin,'position')} style={{cursor:'pointer',marginRight:8}}/>}
                                {POSITION_OPTIONS[admin.position]||'-'}
                            </td>
                            <td style={{fontWeight:600}}>
                                {formatDate(admin.hired)||'-'}
                            </td>
                            {isEditable && (
                                <td>
                                    <button className="btn-delete" onClick={()=>handleDelete(admin)}><i className="bi bi-trash"/></button>
                                </td>
                            )}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default AdminTable
